'use client'

import { useEffect, useSyncExternalStore } from 'react'
import { useScroll, useMotionValueEvent } from 'framer-motion'
import { findNavbar } from '@/hooks/use-navbar-height'

/** Scroll offset before the navbar may hide — shared by every navbar and sticky peers. */
export const NAVBAR_SCROLL_HIDE_OFFSET = 150

/**
 * Accumulated upward pixels required to reveal a hidden navbar.
 * Scroll-anchoring / sticky-header collapse often nudges `scrollY` back by a
 * few pixels during a downward scroll; treating that as a user scroll-up would
 * flash the bar (and the products title) back into view. A deliberate flick up
 * easily exceeds this; a 1–10px clamp does not.
 */
export const NAVBAR_SCROLL_REVEAL_DELTA_PX = 12

/** Matches the framer-motion transition on `NavbarLinks` / `NavbarAuth` / `NavbarDashboard`. */
export const NAVBAR_HIDE_DURATION_S = 0.35

/** Depth counter so nested / rapid programmatic scrolls stay frozen until all finish.
 * Module-scoped: every `useNavbarScrollHidden` consumer shares one freeze gate.
 */
let freezeDepth = 0

/** Accumulated upward delta while the bar is hidden — see NAVBAR_SCROLL_REVEAL_DELTA_PX. */
let revealAccumulatedPx = 0

/**
 * One hidden flag for every consumer (navbar + sticky peers). Separate
 * `useState`s desync when two `useMotionValueEvent` listeners run on the same
 * jump — framer-motion can advance `scrollY.getPrevious()` between them, so the
 * bar translates away while a peer still thinks it is shown and leaves sticky
 * `top` at the full bar height (chips floating mid-viewport over the grid).
 */
let sharedHidden = false
const hiddenListeners = new Set<() => void>()

/** Skip duplicate samples so two hook instances cannot double-count hysteresis. */
let lastProcessedScrollY: number | null = null

function applyHidden(next: boolean): void {
  if (sharedHidden === next) return
  sharedHidden = next
  for (const listener of hiddenListeners) listener()
}

function subscribeHidden(onStoreChange: () => void): () => void {
  hiddenListeners.add(onStoreChange)
  return () => {
    hiddenListeners.delete(onStoreChange)
  }
}

function getHiddenSnapshot(): boolean {
  return sharedHidden
}

/** SSR and the hydration pass always render the bar visible — the browser may
 *  restore scroll (or framer-motion may sample scrollY) before React hydrates,
 *  mutating `sharedHidden` while the HTML still has `data-navbar-hidden="false"`. */
function getServerHiddenSnapshot(): boolean {
  return false
}

/** Test-only: clear leftover freeze depth, reveal hysteresis, and shared hide. */
export function resetNavbarScrollFreezeForTests(): void {
  freezeDepth = 0
  revealAccumulatedPx = 0
  sharedHidden = false
  lastProcessedScrollY = null
}

/** True while category-chip (or other) programmatic scrolls should ignore direction. */
export function isNavbarScrollDirectionFrozen(): boolean {
  return freezeDepth > 0
}

/**
 * Freeze navbar hide/show while a programmatic scroll runs (e.g. products category chips).
 * Without this, an upward `scrollIntoView` looks like a user scroll-up and reveals the navbar.
 * Returns an idempotent unfreeze — call on `scrollend` or after a fallback timeout.
 */
export function freezeNavbarScrollDirection(): () => void {
  freezeDepth += 1
  let released = false
  return () => {
    if (released) return
    released = true
    freezeDepth = Math.max(0, freezeDepth - 1)
  }
}

/**
 * Run `scrollFn` with navbar direction updates frozen until scroll settles.
 * Prefers the `scrollend` event; falls back to `fallbackMs` where unsupported.
 */
export function runWithNavbarScrollFrozen(
  scrollFn: () => void,
  fallbackMs = 1000,
): void {
  if (typeof window === 'undefined') {
    scrollFn()
    return
  }

  const unfreeze = freezeNavbarScrollDirection()
  let done = false
  const release = () => {
    if (done) return
    done = true
    window.clearTimeout(timer)
    window.removeEventListener('scrollend', release)
    unfreeze()
  }

  window.addEventListener('scrollend', release)
  const timer = window.setTimeout(release, fallbackMs)
  scrollFn()
}

/**
 * Same hide-on-scroll-down / show-on-scroll-up rule the fixed navbars use.
 * Hide state is module-scoped (one flag, many subscribers) so the navbar and
 * sticky peers cannot disagree on a single scroll sample.
 *
 * A pointer press anywhere outside the bar hides it too, on the same rule as a
 * scroll down: only once past `NAVBAR_SCROLL_HIDE_OFFSET`, so the bar still rests
 * open at the top of a page. Presses inside the bar are ignored — its own links,
 * burger and user menu have to stay clickable. Presses inside a sticky *peer*
 * (any element carrying `data-navbar-hidden`, e.g. the mobile products header
 * that now collapses along with the bar — see ProductsStickyHeader) are ignored
 * for the same reason: a peer's own controls (category chips, the Filters
 * button) would otherwise vanish out from under the very press that activates
 * them. The listener is registered in the capture phase so a handler that stops
 * propagation (menus, modals) can't swallow it, and every consumer on the page
 * sees the same event, so sticky peers flip in the same commit as the bar
 * instead of desyncing.
 *
 * A downward scroll must not flash the bar back: equal `scrollY`, and reverse
 * nudges smaller than `NAVBAR_SCROLL_REVEAL_DELTA_PX` (accumulated), are ignored
 * so scroll-anchoring / sticky-header collapse cannot look like a user scroll-up.
 * A real flick up still reveals; returning to the top always reveals.
 *
 * The freeze gate deliberately does not apply here: it exists to stop a
 * *programmatic* scroll from being read as a user scroll-up that reveals the bar,
 * and a press only ever hides — the direction the freeze already protects.
 */
export function useNavbarScrollHidden(): boolean {
  const { scrollY } = useScroll()
  const hidden = useSyncExternalStore(
    subscribeHidden,
    getHiddenSnapshot,
    getServerHiddenSnapshot,
  )

  useMotionValueEvent(scrollY, 'change', (latest) => {
    if (freezeDepth > 0) return
    // One sample must only run the direction logic once, even when navbar +
    // sticky header both subscribe — otherwise reveal hysteresis double-counts
    // and a 6px reverse nudge looks like a user flick-up.
    if (lastProcessedScrollY === latest) return
    lastProcessedScrollY = latest
    const previous = scrollY.getPrevious() ?? 0

    // Back at the top of the page: always show, and drop any leftover upward
    // accumulation from a previous hide.
    if (latest <= NAVBAR_SCROLL_HIDE_OFFSET) {
      revealAccumulatedPx = 0
      applyHidden(false)
      return
    }

    if (latest > previous) {
      revealAccumulatedPx = 0
      applyHidden(true)
      return
    }

    if (latest < previous) {
      revealAccumulatedPx += previous - latest
      if (revealAccumulatedPx >= NAVBAR_SCROLL_REVEAL_DELTA_PX) {
        revealAccumulatedPx = 0
        applyHidden(false)
      }
    }
  })

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (window.scrollY <= NAVBAR_SCROLL_HIDE_OFFSET) return
      const target = event.target
      if (!(target instanceof Node)) {
        applyHidden(true)
        return
      }
      const navbar = findNavbar()
      if (navbar?.contains(target)) return
      for (const peer of document.querySelectorAll('[data-navbar-hidden]')) {
        if (peer.contains(target)) return
      }
      applyHidden(true)
    }

    window.addEventListener('pointerdown', onPointerDown, true)
    return () => window.removeEventListener('pointerdown', onPointerDown, true)
  }, [])

  return hidden
}
